import { marked } from 'marked'
import { IS_DEV, IS_STAGE, LOCAL_ENV } from '@/modules/env'
import { getPreferences } from '@/modules/storage'
import { getAuthToken } from '@/auth'
import api from '@/api'

marked.setOptions({ gfm: true, breaks: true })

// Renders assistant Markdown to HTML. Escapes angle brackets first so raw HTML
// (and any script) in the model output is neutralised — marked only ever sees markdown.
export const renderMarkdown = text => {
  const escaped = String(text ?? '').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return marked.parse(escaped)
}

const HOSTS = {
  dev: 'https://chat.wisk.dev',
  stage: 'https://chat.wisk.cool',
  prod: 'https://chat.wisk.ai'
}

// ai_chat_available is the effective entitlement wisk-api computes (the per-user flag, plus
// super users always). ai_chat_enabled is the raw stored flag and drives the admin toggle only.
// The UI merely hides the widget — the chat service refuses a request from a user without access.
export const isAiChatEnabled = user => !!user?.ai_chat_available

export const AI_CHAT_CAP_REACHED = 'ai_chat_cap_reached'

// The venue's standing against its monthly allowance ({ used_cents, limit_cents, percent_used,
// warning, cap_reached, resets_at }), or null when it could not be loaded. The chat service
// enforces the cap; this only drives the warning and the blocked state in the UI.
export const fetchAiChatUsage = async venueId => {
  if (!venueId) return null
  try {
    const access = await api.getAiChatAccess(venueId)
    return access?.usage || null
  } catch {
    return null
  }
}

// The allowance resets at midnight UTC on the 1st, so the date is read in UTC — local time would
// show the last day of the month for anyone west of Greenwich. Formatted in the app's language,
// not the browser's, so it reads naturally inside the translated sentence around it.
export const formatAiChatResetDate = (iso, language) => {
  const date = iso ? new Date(iso) : null
  if (!date || Number.isNaN(date.getTime())) return ''
  const options = { month: 'long', day: 'numeric', timeZone: 'UTC' }
  try {
    return date.toLocaleDateString(language || undefined, options)
  } catch {
    return date.toLocaleDateString(undefined, options)
  }
}

export const aiChatBaseURL = () => {
  const override = getPreferences('environment')
  if (override === LOCAL_ENV.STAGE) return HOSTS.stage
  if (override === LOCAL_ENV.DEV) return HOSTS.dev
  if (override === LOCAL_ENV.PROD) return HOSTS.prod
  if (IS_DEV) return HOSTS.dev
  if (IS_STAGE) return HOSTS.stage
  return HOSTS.prod
}

/**
 * Streams an AI chat reply over Server-Sent Events.
 * Calls onTurn with the turn id (first event), onText for each token, onTool when the assistant
 * invokes a WISK tool. Resolves with the accumulated response text.
 */
export const streamChat = async ({ message, history = [], venue, onTurn, onText, onTool, signal }) => {
  const token = await getAuthToken()
  const response = await fetch(`${aiChatBaseURL()}/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: token } : {})
    },
    body: JSON.stringify({ message, history, venue }),
    signal
  })

  if (response.status === 429) {
    const body = await response.json().catch(() => ({}))
    if (body?.code === AI_CHAT_CAP_REACHED) {
      const error = new Error(body.error || "Your venue has used this month's WISK AI allowance.")
      error.code = AI_CHAT_CAP_REACHED
      error.usage = body.usage
      throw error
    }
  }

  if (!response.ok || !response.body) {
    throw new Error(`AI chat request failed (${response.status})`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let fullText = ''

  const handleEvent = block => {
    const dataLine = block.split('\n').find(line => line.startsWith('data:'))
    if (!dataLine) return
    let payload
    try {
      payload = JSON.parse(dataLine.slice('data:'.length).trim())
    } catch {
      return
    }
    if (payload.error) {
      throw new Error(payload.error)
    }
    if (payload.type === 'turn' && payload.turnId) {
      onTurn?.(payload.turnId)
    } else if (payload.type === 'text' && payload.text) {
      fullText += payload.text
      onText?.(payload.text)
    } else if (payload.type === 'tool' && payload.name) {
      onTool?.(payload.name)
    }
  }

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let separator
    while ((separator = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, separator)
      buffer = buffer.slice(separator + 2)
      handleEvent(block)
    }
  }

  return fullText
}

export const FEEDBACK_CATEGORIES = [
  { id: 'incorrect_data', label: 'Incorrect data' },
  { id: 'did_not_answer', label: "Didn't answer the question" },
  { id: 'error', label: 'Error / failed' },
  { id: 'too_slow', label: 'Too slow' },
  { id: 'other', label: 'Other' }
]

const appVersion = () => {
  const build = import.meta.env.BUILD_TIMESTAMP || ''
  const commit = String(import.meta.env.COMMITHASH || '').slice(0, 8)
  return ['web', build, commit].filter(Boolean).join(' ')
}

/**
 * Sends a thumbs-up / thumbs-down on one assistant answer. The chat service attaches the tool calls
 * it recorded for turnId and stores the report in wisk-api. Rejects if the report was not saved.
 */
export const sendChatFeedback = async ({ venue, rating, category, comment, turnId, message, response, error, history = [], toolNames = [] }) => {
  const token = await getAuthToken()
  const result = await fetch(`${aiChatBaseURL()}/chat/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: token } : {})
    },
    body: JSON.stringify({ venue, rating, category, comment, turnId, message, response, error, history, toolNames, appVersion: appVersion() })
  })
  if (!result.ok) {
    throw new Error(`Feedback request failed (${result.status})`)
  }
  return result.json()
}
